import { useQuery } from '@apollo/client';

import {
  TeamMembersRequestStatus,
  TeamMembersRequestType,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';

import {
  OngoingPaymentCardsRequestsDocument,
  PhysicalPaymentCardOrderRequestItemFragment,
  VirtualBudgetCardOrderRequestItemFragment,
  VirtualPaymentCardOrderRequestItemFragment,
} from './ongoingPaymentCardsRequests.gql';

const useOngoingPaymentCardsRequests = () => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is not defined in "usePaymentCardsOrders"',
    );
  }

  const { data, ...hook } = useQuery(OngoingPaymentCardsRequestsDocument, {
    skip: !companyProfileId,
    variables: { companyProfileId },
  });

  const ongoingPaymentCardsRequests =
    data?.viewer.company.teamMembersRequests.edges.map(
      // The following casting is to better represent the expected type.
      (edge) => edge.node as OngoingPaymentCardRequest,
    ) ?? [];

  return {
    ongoingPaymentCardsRequests,
    ...hook,
  };
};

export default useOngoingPaymentCardsRequests;

export const refetchOngoingPaymentCardRequestQuery = (
  companyProfileId: string,
) => ({
  query: OngoingPaymentCardsRequestsDocument,
  variables: { companyProfileId },
});

export type OngoingPaymentCardRequest = (
  | PhysicalPaymentCardOrderRequestItemFragment
  | VirtualPaymentCardOrderRequestItemFragment
  | VirtualBudgetCardOrderRequestItemFragment
) & {
  status: TeamMembersRequestStatus.Approved | TeamMembersRequestStatus.Pending;
};

export const isVirtualBudgetCardOrderRequest = (
  request: OngoingPaymentCardRequest,
): request is VirtualBudgetCardOrderRequestItemFragment & {
  status: TeamMembersRequestStatus.Approved | TeamMembersRequestStatus.Pending;
} => {
  return request.type === TeamMembersRequestType.VirtualBudgetCardOrder;
};
